import { createContext, type FC, type ReactNode, useContext } from 'react'
import classNames from 'classnames'
import { type ArrayFieldTemplateItemType } from '@rjsf/utils'
import { FormHint } from './FormHint'
import { AddItemLink } from './AddItemLink'
import { ItemRemoveButton } from './IconButtons'
import { useExpandedItems } from './useExpandedItems'
import styles from './ExpandableRows.module.scss'

const ListDepthContext = createContext(0)

interface ExpandableRowsProps {
  items: ArrayFieldTemplateItemType[]
  canAdd: boolean
  locked: boolean
  noun: string
  emptyText: string
  errorIndexes: number[]
  bodyIdPrefix: string
  generation: number
  renderSummary: (item: ArrayFieldTemplateItemType) => ReactNode
  onAddClick: () => void
}

export const ExpandableRows: FC<ExpandableRowsProps> = ({
  items,
  canAdd,
  locked,
  noun,
  emptyText,
  errorIndexes,
  bodyIdPrefix,
  generation,
  renderSummary,
  onAddClick,
}) => {
  const expanded = useExpandedItems(items, errorIndexes, generation)
  const depth = useContext(ListDepthContext)

  return (
    <div className='d-flex flex-column zocks-gap-0.75 pt-2'>
      {items.length === 0 && emptyText ? <FormHint>{emptyText}</FormHint> : null}
      {items.map((item) => {
        const readOnly = Boolean(item.readonly)
        const open = readOnly || expanded.isOpen(item)
        const bodyId = `${bodyIdPrefix}_${item.index}_body`
        const summaryId = `${bodyIdPrefix}_${item.index}_summary`
        return (
          <div
            key={item.key}
            className={classNames('position-relative border zocks-border-color-gray-300 rounded-3', {
              [styles.closedRow]: !open,
              'zocks-bg-color-gray-100': open && depth > 0,
            })}
          >
            <div className='d-flex align-items-center zocks-gap-0.5 ps-3 pe-2 py-2'>
              {readOnly ? (
                <span id={summaryId} className='paragraph-small bold text-truncate flex-grow-1 mw-0'>
                  {renderSummary(item)}
                </span>
              ) : (
                <button
                  type='button'
                  className={classNames(
                    'd-flex align-items-center justify-content-between zocks-gap-0.5 flex-grow-1 mw-0 border-0 bg-transparent p-0 text-start',
                    {
                      'stretched-link': !open,
                    },
                  )}
                  aria-expanded={open}
                  aria-controls={bodyId}
                  aria-disabled={expanded.heldOpen(item)}
                  onClick={() => {
                    if (!expanded.heldOpen(item)) {
                      expanded.toggle(item.key)
                    }
                  }}
                >
                  <span id={summaryId} className='paragraph-small bold text-truncate'>
                    {renderSummary(item)}
                  </span>
                  <i className={classNames('bi flex-shrink-0', open ? 'bi-chevron-up' : 'bi-chevron-down')} aria-hidden='true' />
                </button>
              )}
              <ItemRemoveButton item={item} noun={noun} className='position-relative z-2' />
            </div>
            <div id={bodyId} className={classNames('px-3 pt-2 pb-1', { 'd-none': !open })}>
              <ListDepthContext.Provider value={depth + 1}>{item.children}</ListDepthContext.Provider>
            </div>
          </div>
        )
      })}
      {canAdd ? <AddItemLink noun={noun} disabled={locked} onClick={onAddClick} /> : null}
    </div>
  )
}
